import {
  BMButton,
  BMCheckbox,
  BMComponentsType,
  BMDivider,
  BMInputField,
  BMPrimitives,
  BMText,
  BMTray,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemSearch from "@traveloka/icon-kit-web/react/IcSystemSearch";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FlatList, Pressable, StyleSheet } from "react-native";
import BMDropdownNotFoundOptions from "../../../../components/BMDropdownField/components/BMDropdownNotFoundOptions";
import HighlightText from "../../../../components/HighlightText/HighlightText";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { appendTestId, formatMessage } from "../../../../utils";
import { Options } from "../../PostBooking/OngoingBooking/OngoingBookingFilter/types";

const ITEM_HEIGHT = 44;

type Props = {
  options: Options;
  isVisible: boolean;
  setVisible: (visible: boolean) => void;
  name: string;
  title?: string;
  inputPlaceholder?: string;
  value: string[];
  onChange: (value: string[]) => void;
  testID?: string;
};

export default function BMCheckboxSelection({
  isVisible,
  setVisible,
  options,
  title,
  inputPlaceholder,
  value,
  onChange,
  testID,
}: Props) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchItems, setSearchItems] = useState("");

  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);

  const { CorporateCommon: cr } = useMobileContentResource();

  const allSelected = selectedItems.length === options.length;
  const notSelected = selectedItems.length === 0;
  const indeterminate = !notSelected && !allSelected;

  function handleOnSelectAll() {
    if (allSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(options.map((option) => option.value));
    }
  }

  function handleCloseTray() {
    setVisible(false);
  }

  const filteredData = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(searchItems.toLowerCase())
      ),
    [options, searchItems]
  );

  const getItemLayout = useCallback(
    (_: any, index: number) => ({
      length: ITEM_HEIGHT,
      offset: ITEM_HEIGHT * index,
      index,
    }),
    []
  );

  const renderItem = useCallback(
    () =>
      renderItemFactory(
        selectedItems,
        setSelectedItems,
        searchItems,
        options,
        styles,
        testID
      ),
    [styles, searchItems, options, selectedItems, setSelectedItems]
  );

  useEffect(() => {
    if (isVisible) {
      setSelectedItems(value);
      setSearchItems("");
    } else {
      setSearchItems("");
      setSelectedItems([]);
    }
  }, [isVisible]);

  return (
    <BMTray
      isVisible={isVisible}
      onTrayClose={handleCloseTray}
      initialHeight={620}
      footerComponent={() => (
        <BMView flex row spacing="sm">
          <BMView flex>
            <BMButton
              stretchable
              size="LARGE"
              label={cr.cancelText}
              onPress={handleCloseTray}
              role="SECONDARY"
              testID={appendTestId(testID, "cancel")}
            />
          </BMView>
          <BMView flex>
            <BMButton
              stretchable
              size="LARGE"
              label={cr.confirmText}
              onPress={() => {
                onChange(selectedItems);
                handleCloseTray();
              }}
              testID={appendTestId(testID, "confirm")}
            />
          </BMView>
        </BMView>
      )}
    >
      <BMView spacing="xl">
        <BMView spacing="md">
          {title && <BMText variant="titleNormal">{title}</BMText>}
          <BMInputField
            LeadingIcon={IcSystemSearch}
            value={searchItems}
            placeholder={inputPlaceholder}
            onChangeText={setSearchItems}
            testID={appendTestId(testID, "search_input")}
          />
        </BMView>
        {!!filteredData.length && (
          <BMView>
            <Pressable onPress={handleOnSelectAll}>
              <BMView style={styles.selectAllContainer}>
                <BMCheckbox
                  indeterminate={indeterminate}
                  value={allSelected}
                  label={formatMessage(cr.selectAllParentheses, {
                    num: options.length,
                  })}
                  onChange={handleOnSelectAll}
                  customTextColor={{
                    label: indeterminate
                      ? BMPrimitives.BMBrand.productHotels
                      : allSelected
                      ? BMComponents.BMStyleTextInkAction
                      : undefined,
                  }}
                  testID={appendTestId(testID, "select_all")}
                />
              </BMView>
            </Pressable>
            <BMDivider type="SUBTLE" />
          </BMView>
        )}
        <BMView>
          <FlatList
            data={filteredData}
            ListEmptyComponent={
              <BMDropdownNotFoundOptions query={searchItems} isMobile />
            }
            renderItem={renderItem()}
            keyExtractor={(item) => item.value.toString()}
            windowSize={10}
            initialNumToRender={15}
            maxToRenderPerBatch={10}
            removeClippedSubviews={false}
            getItemLayout={getItemLayout}
          />
        </BMView>
      </BMView>
    </BMTray>
  );
}

type RenderItemProps = {
  item: {
    value: string;
    label: string;
  };
  index: number;
};

function renderItemFactory(
  selectedItems: string[],
  setSelectedItems: (items: string[]) => void,
  searchItems: string,
  options: RenderItemProps["item"][],
  styles: ReturnType<typeof createStyles>,
  testID?: string
) {
  return function ({ item, index }: RenderItemProps) {
    const selected = selectedItems.includes(item.value);

    function handleOnChange() {
      if (selectedItems.includes(item.value)) {
        setSelectedItems(
          selectedItems.filter((option) => option !== item.value)
        );
      } else {
        setSelectedItems([...selectedItems, item.value]);
      }
    }

    return (
      <Fragment key={index}>
        <Pressable
          onPress={handleOnChange}
          testID={appendTestId(testID, `option-${index}`)}
        >
          <BMView style={[styles.item, selected && styles.activeItem]}>
            <BMView row align="center">
              {/* BMView is needed since checkbox will expand to full width on android if not */}
              <BMView>
                <BMCheckbox value={selected} onChange={handleOnChange} />
              </BMView>
              <HighlightText
                keyword={searchItems}
                searchedText={item.label}
                defaultTextProps={{
                  ellipsizeMode: "tail",
                  style: selected ? styles.activeText : undefined,
                }}
                highlightTextProps={{
                  style: styles.highlightText,
                  ellipsizeMode: "tail",
                }}
              />
            </BMView>
          </BMView>
        </Pressable>
        {index < options.length - 1 && <BMDivider type="SUBTLE" />}
      </Fragment>
    );
  };
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    selectAllContainer: {
      paddingVertical: BMPrimitives.BMSize.md,
    },
    item: {
      paddingVertical: BMPrimitives.BMSize.md,
      paddingHorizontal: BMPrimitives.BMSize.sm,
    },
    activeItem: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorHighlight,
    },
    activeText: {
      color: BMComponents.BMStyleTextInkAction,
    },
    highlightText: {
      color: BMPrimitives.BMBrand.primary,
    },
  });
}
